import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { navigateToPath } from '../utils/routing';
import { performLogout } from '../hooks/useLoginState';
import { useNavigate } from "react-router-dom";

import './../assets/css/InactivityContext.scss';

// Assuming you have an auth context or service for logout and navigation
// import { useAuth } from './AuthContext'; // Example
// import { useNavigate } from 'react-router-dom'; // Example

const INACTIVITY_DURATION_MS = 60 * 60 * 1000; // 1 hour
const WARNING_DURATION_MS = 55 * 60 * 1000;   // 55 minutes (for warning)
// const INACTIVITY_DURATION_MS = 20 * 1000; // For testing: 10 seconds
// const WARNING_DURATION_MS = 15 * 1000;   // For testing: 5 seconds

const InactivityContext = createContext();

export const useInactivity = () => useContext(InactivityContext);

export const InactivityProvider = ({ children }) => {
  // const { logout } = useAuth(); // Example: Get logout function from your auth context
  // const navigate = useNavigate(); // Example: For redirection

  const navigate = useNavigate();

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("FWToken"));
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const handleLogout = useCallback(() => {
    console.log("User inactive, logging out.");
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(warningTimerRef.current);
    setShowWarningModal(false);
    // logout(); // Call your actual logout function
    // navigate('/login'); // Redirect to login
    // alert("You have been logged out due to inactivity."); // Placeholder

    // Use the performLogout helper which handles localStorage and events
    performLogout();

    // Redirect to login page using proper routing utility
    navigate("/login", { replace: true }); // Use replace to avoid back navigation

  }, [/* logout, navigate */]);

  const resetTimers = useCallback(() => {
    // console.log("Activity detected, resetting timers.");
    setShowWarningModal(false);

    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

    warningTimerRef.current = setTimeout(() => {
      console.log("Showing inactivity warning modal.");
      setShowWarningModal(true);
    }, WARNING_DURATION_MS);

    inactivityTimerRef.current = setTimeout(handleLogout, INACTIVITY_DURATION_MS);
  }, [handleLogout]);

  const extendSession = () => {
    console.log("Session extended by user.");
    resetTimers();
  };

  useEffect(() => {
    const activityEvents = [
      'mousemove', 'mousedown', 'keypress',
      'scroll', 'touchstart', 'click'
    ];

    const handleActivity = () => {
      resetTimers();
    };

    // Initialize timers when the component mounts (assuming user is logged in)
    resetTimers();

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [resetTimers]);

  return (
    <InactivityContext.Provider value={{ showWarningModal, extendSession, handleLogout }}>
      {children}
      {/* You would render your actual modal component here */}
      {showWarningModal && isLoggedIn && (
        <div className="inactivity-warning-modal">
          <p>You will be logged out in a few minutes due to inactivity.</p>
          <button onClick={extendSession}>Stay Logged In</button>
          <button onClick={handleLogout} style={{marginLeft: '10px'}}>Logout Now</button>
        </div>
      )}
    </InactivityContext.Provider>
  );
};